import {
  Badge,
  Button,
  Checkbox,
  Collapse,
  CollapseProps,
  Form,
  message,
  Select,
} from "antd";
import EcpAnimationBlock from "../../../EcpAnimationBlock/EcpAnimationBlock";
import { ModalSigningDocs } from "../../../Ui/ModalSigningDocs/ModalSigningDocs";
import React, { useState } from "react";
import { SuccessSignListDocs } from "../../components/SuccessSignListDocs/SuccessSignListDocs";
import { IModalSigningProps } from "../../../../interfaces/props/IIModalSigningProps";
import {
  ModalView,
  useClientSignModalState,
} from "../../../DocumentsSignModal/model/useClientSignModalState";
import { UsbOutlined } from "@ant-design/icons";
import { useDataCrypto } from "../../../../utils/utilities/crypto-pro/useDataCrypto";
import { Certificate } from "crypto-pro";
import { TagTranslation } from "crypto-pro/lib/helpers/_parseCertInfo";
import { SignDocApplicationInfo } from "../../components/SuccessSignListDocs/items/SignDocApplicationMainInfo/SignDocApllicationInfo";
import { SignDocItem } from "../../components/SuccessSignListDocs/items/SignDocItem/SignDocItem";
import { ConfirmText } from "../../../DocumentsSignModal/ui/ConfirmText/ConfirmText";
import { useLazyGetFilteredListApplicationQuery } from "../../../../store/api/application.api";
import { APPLICATION_STATUS_VALUE_FILTER } from "../../../../enums/applicationStatusFilterValueEnum";
import { selectViewShowBy } from "../../../../constants/selectViewShowBy";

import classes from "./ModalSigning.module.css";
import { getDocumentsForSigningFromServer } from "../../../DocumentsSignModal/helpers/getDocumentsForSigningFromServer";
import { verificationModesAfterSign } from "../../../../constants/verificationModesAfterSign";

export const ModalSigning = ({
  applications,
  disabled = false,
}: IModalSigningProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [signingState, setSigningState] = useState<ModalView | null>(null);
  const [trigger] = useLazyGetFilteredListApplicationQuery();
  const [thumbprint, setThumbprint] = React.useState("");
  const { signDocumentsCryptoProAndVerificate } = useClientSignModalState(true);

  const { Option } = Select;
  const { dataCrypto } = useDataCrypto();

  const handleSelectSign = (value: string) => {
    setThumbprint(value);
  };

  const startSignInCSP = async () => {
    setSigningState("ecpAnimation");

    try {
      for await (const docData of applications) {
        try {
          const verificationMode =
            verificationModesAfterSign[docData.status.type];

          await signDocumentsCryptoProAndVerificate(
            docData.id,
            thumbprint,
            verificationMode,
          );
        } catch (error) {
          setModalOpen(false);
          message.error("Подписание остановлено");
        }
      }

      setSigningState("finish");
    } catch (error) {
      setModalOpen(false);
      message.error("Подписание остановлено");
    }
  };

  const finishSignIn = async () => {
    try {
      await trigger({
        page: 1,
        limit: selectViewShowBy[0].value,
        status_preset: APPLICATION_STATUS_VALUE_FILTER.SIGNATURE_REQUIRED,
      });

      setModalOpen(false);
      setSigningState(null);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const signingStateMap = {
    confirmText: {
      title: "Подписать документы",
      children: (
        <div className={classes.modalSigning__confirmWrapper}>
          <ConfirmText setModalView={setSigningState} />
        </div>
      ),
      footer: false,
    },
    ecpAnimation: {
      title: "Выполняется подписание документов",
      children: <EcpAnimationBlock />,
      footer: false,
    },
    finish: {
      title: "Документы успешно подписаны",
      children: <SuccessSignListDocs applications={applications} />,
      footer: (
        <div className={classes.successSigningFooter}>
          <Button
            size="large"
            variant="solid"
            color="primary"
            className={classes.successBtnOk}
            onClick={async () => await finishSignIn()}
          >
            Ок
          </Button>
        </div>
      ),
    },
    selectSign: {
      title: "Подписать документы",
      children: (
        <div className={classes.setCSPWrapper}>
          <Select
            onChange={handleSelectSign}
            onClick={() => null}
            placeholder={
              <React.Fragment>
                <UsbOutlined /> {" "}
                <span className={classes.placeholderSize}> ЭЦП... </span>
              </React.Fragment>
            }
          >
            {dataCrypto?.map((certificate: Certificate) => {
              const cspNames: string[] = [];
              certificate
                .getOwnerInfo()
                .then((decodedOwnerInfo: TagTranslation[]) => {
                  cspNames.push(
                    `${
                      decodedOwnerInfo.find((info) => info.title === "Владелец")
                        ?.description
                    } (ИНН ${
                      decodedOwnerInfo.find((info) => info.title === "ИНН")
                        ?.description
                    })`,
                  );
                });
              return (
                <Option
                  key={certificate.thumbprint}
                  value={certificate.thumbprint}
                >
                  <Form key={certificate.thumbprint}>
                    <Form.Item className={classes.singModalInput}>
                      {cspNames}
                    </Form.Item>
                  </Form>
                </Option>
              );
            })}
          </Select>
          <div className={classes.additionalDocsContainer}>
            {applications &&
              applications?.map((documentData) => {
                const items: CollapseProps["items"] = [
                  {
                    key: documentData.bank_id,
                    label: (
                      <SignDocApplicationInfo
                        applicationNumber={documentData.application_id}
                        sumBg={documentData.number_bg || ""}
                        tenderNumber={431241}
                      />
                    ),
                    classNames: {
                      header: classes.itemDocHeader,
                      body: classes.itemDocBody,
                    },
                    children: documentData.documents.map((el) => {
                      if (el.sign_file && el.sign_file.length) {
                        return;
                      }

                      return (
                        <SignDocItem
                          className={classes.signDocItem}
                          title={el.type_doc.name}
                          subtitle={el.name}
                          key={el.id}
                        />
                      );
                    }),
                  },
                ];

                return (
                  <Collapse
                    expandIconPosition="end"
                    size="large"
                    bordered={false}
                    accordion
                    key={documentData.bank_id}
                    items={items}
                  />
                );
              })}
          </div>
        </div>
      ),
      footer: (
        <div className={classes.buttons}>
          <Button onClick={() => setModalOpen(false)}>Отмена</Button>
          <Button
            disabled={!thumbprint}
            type="primary"
            onClick={startSignInCSP}
          >
            Начать процесс подписания
          </Button>
        </div>
      ),
    },
  };

  const handleChooseCSP = async () => {
    setModalOpen(true);
    setSigningState("confirmText");
  };

  return (
    <>
      <div className={classes.footer}>
        <Button
          onClick={handleChooseCSP}
          variant="solid"
          color="primary"
          size={"large"}
          disabled={disabled}
        >
          Подписать выбранные
        </Button>
      </div>

      <ModalSigningDocs
        width={700}
        open={modalOpen}
        styles={{ body: { height: 600 } }}
        onCancel={() => {
          setModalOpen(false);
          setSigningState(null);
        }}
        title={signingState ? signingStateMap[signingState].title : undefined}
        footer={signingState ? signingStateMap[signingState].footer : undefined}
      >
        {signingState && signingStateMap[signingState].children}
      </ModalSigningDocs>
    </>
  );
};
